import { useState, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import LogGrid from '../../components/grid/LogGrid';
import { ILog } from '../../utils/types/ILog';
import { Helmet } from 'react-helmet-async';
import SkeletonLoader from '../../components/loader/SkeletonLoader';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PagedResult } from '../../utils/types/PagedResult';
import TypeLogSelect from '../../components/select/TypeLogSelect';
import { Spinner } from 'react-bootstrap';
import Pagination from '../../components/others/pagination';
import { IconCategory } from '@tabler/icons-react';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const LogPage = () => {
    const [page, setPage] = useState(1);
    const [level, setLevel] = useState<string | null>('Error');

    // ---

    const handleLevelChange = (newLevel: string | null) => {
        setLevel(newLevel);
        setPage(1);
    };

    // ---

    const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
        queryKey: ['logs', page, level],
        queryFn: async () => {
            const response = await apiClient.get('/api/log/getData', {
                params: { page, pageSize: 250, level: level || undefined },
            });
            return response.data as PagedResult<ILog>;
        },
        meta: { title: 'Chargement des logs' },
        placeholderData: keepPreviousData,
    });

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(() => [{ isError: isError, error: error, title: 'Erreur lors du chargement des logs', refetch: refetch }], [isError, error, refetch]);

    // ---

    return (
        <>
            <Helmet>
                <title>Logs :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col-12 col-md-auto ms-auto d-print-none order-1 order-md-1">
                            <form
                                className="row gy-2 gx-3 align-items-center"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    refetch();
                                }}
                            >
                                <div className="col-auto">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <IconCategory size={16} />
                                        </span>

                                        <TypeLogSelect selectedTypeLog={level} setSelectedTypeLog={handleLevelChange} />
                                    </div>
                                </div>

                                <div className="col-auto">
                                    <button
                                        className="btn btn-6 w-100 btn-icon"
                                        aria-label="Actualiser"
                                        onClick={() => {
                                            refetch();
                                        }}
                                        disabled={isLoading}
                                        type="button"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            </>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                                <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="col order-0 order-md-0">
                            <div className="page-pretitle">Administration</div>
                            <h2 className="page-title">Journal des erreurs</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div>
                        {isLoading ? (
                            <>
                                <p className="text-center">En cours de chargement</p>
                                <SkeletonLoader />
                            </>
                        ) : data && data.data.length > 0 ? (
                            <>
                                <div className="mb-2">
                                    <Pagination page={data.page} totalPages={data.totalPages} totalCount={data.totalCount} onPageChange={setPage} isFetching={isFetching} />
                                </div>

                                <LogGrid logs={data.data} />
                            </>
                        ) : (
                            <div className="alert alert-warning" role="alert">
                                <div className="alert-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                        <path d="M12 9v4"></path>
                                        <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                        <path d="M12 16h.01"></path>
                                    </svg>
                                </div>
                                Aucune donnée.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

LogPage.Layout = DefaultLayout;

export default LogPage;
